import React, { useEffect, useMemo, useState, useCallback } from "react";
import { apiGet, apiPost } from "../../utils/apiClient";

type Row = {
    emailLower: string;
    enabled: boolean;
    memo: string;

    allTenants?: boolean;
    tenants?: string[];

    createdAt: string | null;
    updatedAt: string | null;
};

function isValidEmail(s: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function uniq(xs: string[]) {
    return Array.from(new Set(xs));
}

function parseTenants(input: string): string[] {
    const parts = input
        .split(/[, \n\t]+/)
        .map((x) => String(x ?? "").trim())
        .filter(Boolean);
    return uniq(parts);
}

function fmtUpdatedAt(v?: string | null) {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    // compact
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${y}/${m}/${dd} ${hh}:${mm}`;
}

export default function SuperUsersPage() {
    const [rows, setRows] = useState<Row[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);

    // add form
    const [email, setEmail] = useState("");
    const [memo, setMemo] = useState("");
    const [enabled, setEnabled] = useState(true);
    const [allTenants, setAllTenants] = useState(false);
    const [tenantsText, setTenantsText] = useState("");

    // compact filters
    const [q, setQ] = useState("");
    const [onlyEnabled, setOnlyEnabled] = useState(false);
    const [onlyDisabled, setOnlyDisabled] = useState(false);
    const [onlyAllTenants, setOnlyAllTenants] = useState(false);

    const canAdd = useMemo(() => isValidEmail(email) && !saving, [email, saving]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const res = await apiGet("/api/super/superusers");
            const data = await res.json();

            if (!res.ok || !data?.ok) {
                setError(data?.error || "読み込みに失敗しました");
                setRows([]);
                return;
            }

            setCurrentTenantId(typeof data?.tenantId === "string" ? data.tenantId : null);
            setRows(Array.isArray(data.rows) ? data.rows : []);
        } catch (e: any) {
            setError(e?.message ?? "通信エラー");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const upsert = useCallback(
        async (payload: {
            email: string;
            enabled?: boolean;
            memo?: string;
            allTenants?: boolean;
            tenants?: string[];
        }) => {
            try {
                setSaving(true);
                const res = await apiPost("/api/super/superusers/set", payload);
                const data = await res.json();
                if (!res.ok || !data?.ok) {
                    alert(data?.error || "更新に失敗しました");
                    return false;
                }
                return true;
            } catch (e: any) {
                alert(e?.message ?? "通信エラー");
                return false;
            } finally {
                setSaving(false);
            }
        },
        []
    );

    const remove = useCallback(
        async (targetEmail: string) => {
            if (!window.confirm(`${targetEmail} を削除します。よろしいですか？`)) return;
            try {
                setSaving(true);
                const res = await apiPost("/api/super/superusers/remove", { email: targetEmail });
                const data = await res.json();
                if (!res.ok || !data?.ok) {
                    alert(data?.error || "削除に失敗しました");
                    return;
                }
                await load();
            } catch (e: any) {
                alert(e?.message ?? "通信エラー");
            } finally {
                setSaving(false);
            }
        },
        [load]
    );

    const onAdd = useCallback(async () => {
        if (!isValidEmail(email)) {
            alert("メールアドレスが不正です");
            return;
        }

        const tenants = allTenants ? [] : parseTenants(tenantsText);
        if (!allTenants && tenants.length === 0) {
            if (currentTenantId) tenants.push(currentTenantId);
        }
        if (!allTenants && tenants.length === 0) {
            alert("許可テナントを入力してください（例: ecx）または「全テナント」をONにしてください");
            return;
        }

        const ok = await upsert({
            email,
            enabled,
            memo,
            allTenants,
            tenants,
        });

        if (ok) {
            setEmail("");
            setMemo("");
            setEnabled(true);
            setAllTenants(false);
            setTenantsText("");
            await load();
        }
    }, [email, enabled, memo, allTenants, tenantsText, currentTenantId, upsert, load]);

    // filter view
    const visibleRows = useMemo(() => {
        const k = q.trim().toLowerCase();
        return rows
            .filter((r) => {
                if (onlyEnabled && !r.enabled) return false;
                if (onlyDisabled && r.enabled) return false;
                if (onlyAllTenants && !r.allTenants) return false;

                if (!k) return true;

                const scope = r.allTenants
                    ? "all"
                    : Array.isArray(r.tenants)
                        ? r.tenants.join(",")
                        : "";

                const hay = `${r.emailLower} ${r.memo ?? ""} ${scope}`.toLowerCase();
                return hay.includes(k);
            })
            .sort((a, b) => (a.emailLower || "").localeCompare(b.emailLower || "", "en"));
    }, [rows, q, onlyEnabled, onlyDisabled, onlyAllTenants]);

    const selectedHint = useMemo(() => {
        if (!q && !onlyEnabled && !onlyDisabled && !onlyAllTenants) return null;
        return `表示: ${visibleRows.length} / 全 ${rows.length}`;
    }, [visibleRows.length, rows.length, q, onlyEnabled, onlyDisabled, onlyAllTenants]);

    if (loading) {
        return <div className="text-xs text-gray-600 p-2">読み込み中...</div>;
    }

    // compact classes
    const wrap = "h-full min-h-0 flex flex-col gap-2 overflow-hidden p-2";
    const card = "bg-white border rounded-md px-2 py-2";
    const label = "block text-[11px] text-gray-600 mb-1";
    const input = "w-full border rounded px-2 py-1 text-xs";
    const btn = "text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50";
    const btnPrimary = "text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50";
    const th = "px-2 py-1 border-b text-[11px] font-semibold bg-gray-50 sticky top-0 z-10 text-left whitespace-nowrap";
    const td = "px-2 py-1 border-b text-xs align-top";

    return (
        <div className={wrap}>
            {/* Add / Controls */}
            <div className={card}>
                <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                        <h1 className="text-sm font-semibold">スーパーユーザー管理</h1>
                        <div className="text-[11px] text-gray-500">
                            {currentTenantId ? (
                                <>
                                    tenant: <span className="font-mono">{currentTenantId}</span>
                                </>
                            ) : (
                                "tenant: -"
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={load} disabled={saving} className={btn}>
                            再読み込み
                        </button>
                    </div>
                </div>

                {error && <div className="mt-2 text-xs text-red-600">{error}</div>}

                {/* add form (compact grid) */}
                <div className="mt-2 grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                    <div className="md:col-span-4">
                        <label className={label}>メールアドレス</label>
                        <input
                            className={input}
                            placeholder="user@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div className="md:col-span-3">
                        <label className={label}>メモ</label>
                        <input
                            className={input}
                            placeholder="用途や担当など"
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className={label}>有効</label>
                        <select className={input} value={enabled ? "1" : "0"} onChange={(e) => setEnabled(e.target.value === "1")}>
                            <option value="1">有効</option>
                            <option value="0">無効</option>
                        </select>
                    </div>

                    <div className="md:col-span-1 flex items-center gap-2">
                        <input id="allTenants" type="checkbox" checked={allTenants} onChange={(e) => setAllTenants(e.target.checked)} />
                        <label htmlFor="allTenants" className="text-[11px] text-gray-700 select-none">
                            全テナント
                        </label>
                    </div>

                    <div className="md:col-span-1">
                        <label className={label}>許可テナント</label>
                        <input
                            className={`${input} disabled:bg-gray-50`}
                            placeholder={currentTenantId ? `例: ${currentTenantId}` : "例: ecx, ecvision"}
                            value={tenantsText}
                            onChange={(e) => setTenantsText(e.target.value)}
                            disabled={allTenants}
                        />
                    </div>

                    <div className="md:col-span-1">
                        <button onClick={onAdd} disabled={!canAdd} className={`${btnPrimary} w-full`}>
                            追加
                        </button>
                    </div>
                </div>

                <div className="mt-2 text-[11px] text-gray-500">
                    ※「全テナント」= どのテナントでも super 扱い。OFF の場合は「許可テナント」に tenantId（カンマ区切り）を入れてください。
                </div>
            </div>

            {/* Filters (compact) */}
            <div className={`${card} flex flex-wrap items-center gap-2`}>
                <input
                    className="border rounded px-2 py-1 text-xs w-64"
                    placeholder="検索: email / memo / tenant"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />

                <label className="text-xs flex items-center gap-1 select-none">
                    <input
                        type="checkbox"
                        checked={onlyEnabled}
                        onChange={(e) => {
                            const on = e.target.checked;
                            setOnlyEnabled(on);
                            if (on) setOnlyDisabled(false);
                        }}
                    />
                    有効のみ
                </label>

                <label className="text-xs flex items-center gap-1 select-none">
                    <input
                        type="checkbox"
                        checked={onlyDisabled}
                        onChange={(e) => {
                            const on = e.target.checked;
                            setOnlyDisabled(on);
                            if (on) setOnlyEnabled(false);
                        }}
                    />
                    無効のみ
                </label>

                <label className="text-xs flex items-center gap-1 select-none">
                    <input type="checkbox" checked={onlyAllTenants} onChange={(e) => setOnlyAllTenants(e.target.checked)} />
                    ALLのみ
                </label>

                <button
                    className={btn}
                    onClick={() => {
                        setQ("");
                        setOnlyEnabled(false);
                        setOnlyDisabled(false);
                        setOnlyAllTenants(false);
                    }}
                >
                    クリア
                </button>

                <div className="ml-auto text-[11px] text-gray-500">{selectedHint ?? `全 ${rows.length} 件`}</div>
            </div>

            {/* table */}
            <div className="bg-white border rounded-md overflow-hidden flex-1 min-h-0 flex flex-col">
                <div className="overflow-auto flex-1 min-h-0">
                    <table className="min-w-[900px] w-full border-collapse">
                        <colgroup>
                            <col style={{ width: "18rem" }} />
                            <col style={{ width: "6rem" }} />
                            <col style={{ width: "14rem" }} />
                            <col />
                            <col style={{ width: "9rem" }} />
                            <col style={{ width: "12rem" }} />
                        </colgroup>

                        <thead>
                            <tr className="text-left">
                                <th className={th}>Email</th>
                                <th className={th}>状態</th>
                                <th className={th}>スコープ</th>
                                <th className={th}>メモ</th>
                                <th className={th}>更新</th>
                                <th className={th}>操作</th>
                            </tr>
                        </thead>

                        <tbody>
                            {visibleRows.length === 0 ? (
                                <tr>
                                    <td className="px-2 py-6 text-gray-500 text-xs" colSpan={6}>
                                        登録がありません
                                    </td>
                                </tr>
                            ) : (
                                visibleRows.map((r) => {
                                    const scopeText = r.allTenants
                                        ? "ALL"
                                        : Array.isArray(r.tenants) && r.tenants.length > 0
                                            ? r.tenants.join(", ")
                                            : "(未設定)";

                                    return (
                                        <tr key={r.emailLower} className="hover:bg-gray-50">
                                            <td className={`${td} font-mono`}>{r.emailLower}</td>

                                            <td className={td}>
                                                <span className={r.enabled ? "text-green-700 font-semibold" : "text-gray-500"}>
                                                    {r.enabled ? "有効" : "無効"}
                                                </span>
                                            </td>

                                            <td className={`${td} font-mono text-[11px]`} title={scopeText}>
                                                <span className={`inline-flex px-2 py-0.5 rounded border text-[11px] ${r.allTenants ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-gray-50 text-gray-700 border-gray-200"}`}>
                                                    {scopeText}
                                                </span>
                                            </td>

                                            <td className={td}>{r.memo || ""}</td>

                                            <td className={`${td} text-[11px] text-gray-600 font-mono`}>{fmtUpdatedAt(r.updatedAt)}</td>

                                            <td className={td}>
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        disabled={saving}
                                                        className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                                                        onClick={async () => {
                                                            const ok = await upsert({ email: r.emailLower, enabled: !r.enabled });
                                                            if (ok) await load();
                                                        }}
                                                    >
                                                        {r.enabled ? "無効化" : "有効化"}
                                                    </button>

                                                    <button
                                                        disabled={saving}
                                                        className="text-xs text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                                                        onClick={async () => {
                                                            const cur = r.allTenants
                                                                ? "ALL"
                                                                : Array.isArray(r.tenants)
                                                                    ? r.tenants.join(",")
                                                                    : "";

                                                            const input = window.prompt(
                                                                "スコープを編集：\n- ALL と入力 → 全テナント\n- 例: ecx,ecvision\n",
                                                                cur
                                                            );
                                                            if (input === null) return;

                                                            const v = input.trim();
                                                            if (v.toUpperCase() === "ALL") {
                                                                const ok = await upsert({ email: r.emailLower, allTenants: true, tenants: [] });
                                                                if (ok) await load();
                                                                return;
                                                            }

                                                            const tenants = parseTenants(v);
                                                            if (tenants.length === 0) {
                                                                alert("テナントが空です（ALL か、tenantId を入力してください）");
                                                                return;
                                                            }

                                                            const ok = await upsert({ email: r.emailLower, allTenants: false, tenants });
                                                            if (ok) await load();
                                                        }}
                                                    >
                                                        テナント編集
                                                    </button>

                                                    <button
                                                        disabled={saving}
                                                        className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                                                        onClick={() => remove(r.emailLower)}
                                                    >
                                                        削除
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="px-2 py-1 text-[11px] text-gray-500 border-t">
                    表示: {visibleRows.length} / 全 {rows.length}（saving中は操作が無効になります）
                </div>
            </div>
        </div>
    );
}
